import React, { useState, useEffect, useMemo, memo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus } from 'lucide-react';

const AddStakeholderForm = memo(function AddStakeholderForm({ 
  open, 
  onOpenChange, 
  segmentTypes, 
  segments, 
  editingStakeholder,
  onSubmit,
  onOpenCreate 
}) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    project_title: '',
    segment_assignments: {},
    invite_to_pulse: true
  });
  const [submitting, setSubmitting] = useState(false);

  // Memoize segments grouped by type for fast lookup
  const segmentsByType = useMemo(() => {
    const byType = {};
    segments.forEach(s => {
      if (!byType[s.segment_type_id]) {
        byType[s.segment_type_id] = [];
      }
      byType[s.segment_type_id].push(s);
    });
    return byType;
  }, [segments]);

  // Reset form when dialog opens/closes or editing stakeholder changes
  useEffect(() => {
    if (open) {
      if (editingStakeholder) {
        const segAssignments = {};
        (editingStakeholder.segment_assignments || []).forEach(sa => {
          segAssignments[sa.segment_type_id] = sa.segment_id;
        });
        setFormData({
          first_name: editingStakeholder.first_name || '',
          last_name: editingStakeholder.last_name || '',
          email: editingStakeholder.email || '',
          project_title: editingStakeholder.project_title || '',
          segment_assignments: segAssignments,
          invite_to_pulse: !editingStakeholder.is_reference_only
        });
      } else {
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          project_title: '',
          segment_assignments: {},
          invite_to_pulse: true
        });
      }
    }
  }, [open, editingStakeholder]);

  const updateSegmentAssignment = (segmentTypeId, segmentId) => {
    setFormData(prev => ({
      ...prev,
      segment_assignments: {
        ...prev.segment_assignments,
        [segmentTypeId]: segmentId
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Convert segment_assignments object to array format
    const segmentAssignmentsArray = Object.entries(formData.segment_assignments)
      .filter(([_, segmentId]) => segmentId)
      .map(([segmentTypeId, segmentId]) => ({
        segment_type_id: segmentTypeId,
        segment_id: segmentId
      }));

    const submitData = {
      ...formData,
      segment_assignments: segmentAssignmentsArray
    };

    try {
      await onSubmit(submitData, editingStakeholder);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  const getSegmentsForType = (typeId) => {
    return segmentsByType[typeId] || [];
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button 
          className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
          onClick={onOpenCreate}
          data-testid="create-stakeholder-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Stakeholder
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
            {editingStakeholder ? 'Edit Stakeholder' : 'Add New Stakeholder'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                placeholder="John"
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                required
                data-testid="stakeholder-firstname-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                placeholder="Doe"
                value={formData.last_name}
                onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                required
                data-testid="stakeholder-lastname-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email (optional for reference-only)</Label>
            <Input
              id="email"
              type="email"
              placeholder="john@company.com"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              data-testid="stakeholder-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="project_title">Project Title / Role</Label>
            <Input
              id="project_title"
              placeholder="e.g., Project Sponsor, Team Lead"
              value={formData.project_title}
              onChange={(e) => setFormData(prev => ({ ...prev, project_title: e.target.value }))}
              data-testid="stakeholder-project-title-input"
            />
          </div>
          
          {/* Segment Assignments - One per Type */}
          {segmentTypes.length > 0 && (
            <div className="space-y-3">
              <Label>Segment Assignments</Label>
              <p className="text-xs text-slate-500 -mt-1">
                Assign one segment per type
              </p>
              <div className="space-y-3 max-h-48 overflow-y-auto border rounded-md p-3">
                {segmentTypes.map(type => {
                  const typeSegments = getSegmentsForType(type.id);
                  return (
                    <div key={type.id} className="space-y-1.5">
                      <Label className="text-xs font-medium text-slate-600">{type.name}</Label>
                      <Select
                        value={formData.segment_assignments[type.id] || 'none'}
                        onValueChange={(value) => updateSegmentAssignment(type.id, value === 'none' ? '' : value)}
                      >
                        <SelectTrigger className="h-9" data-testid={`segment-select-${type.id}`}>
                          <SelectValue placeholder={`Select ${type.name}...`} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {typeSegments.map(segment => (
                            <SelectItem key={segment.id} value={segment.id}>
                              {segment.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between py-2">
            <div>
              <Label htmlFor="invite_to_pulse">Invite to Pulse?</Label>
              <p className="text-xs text-slate-500">Will receive survey invitations</p>
            </div>
            <Switch
              id="invite_to_pulse"
              checked={formData.invite_to_pulse}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, invite_to_pulse: checked }))}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              disabled={submitting}
              data-testid="stakeholder-submit-btn"
            >
              {submitting ? 'Saving...' : (editingStakeholder ? 'Update' : 'Create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
});

export default AddStakeholderForm;
